import React, { useEffect, useState } from "react";
import { Droplet, Circle, Edit3, Trash2, ChevronRight } from "lucide-react";
import {
    listQuantityLogs, listQuantityHistory,
    createQuantityLog, updateQuantityLog, deleteQuantityLog,
} from "@/lib/api";
import { fmtHM, toLocalTimeInput, todayKey, fmtHistoryDate } from "./helpers";

export default function QuantityTracker({ goal }) {
    const qc = goal.quantity_config || { target_per_day: 100, unit: "oz", increment: 8, icon: "water" };
    const target = Number(qc.target_per_day) || 100;
    const inc = Number(qc.increment) || 8;
    const unit = qc.unit || "oz";

    const [logs, setLogs] = useState([]);
    const [history, setHistory] = useState([]);
    const [openLogId, setOpenLogId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [customAmt, setCustomAmt] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => {
        listQuantityLogs(goal.id, todayKey()).then(d => setLogs(d.logs || []));
        listQuantityHistory(goal.id).then(d => setHistory((d.days || []).filter(x => x.date !== todayKey())));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [goal.id]);

    const total = logs.reduce((s, l) => s + Number(l.amount || 0), 0);
    const remaining = Math.max(0, target - total);

    // Icon grid: ceil(target/inc); last icon may be a remainder amount
    const iconCount = Math.max(1, Math.ceil(target / inc));
    const remainder = target - inc * (iconCount - 1);
    const iconAmounts = Array.from({ length: iconCount }, (_, i) =>
        i === iconCount - 1 ? remainder : inc
    );

    // Fill icons left-to-right based on today's total
    const filledIcons = [];
    let remainingTotal = total;
    for (let i = 0; i < iconCount; i++) {
        const canonical = iconAmounts[i];
        if (remainingTotal >= canonical) {
            filledIcons.push({ i, filled: 1 });
            remainingTotal -= canonical;
        } else if (remainingTotal > 0) {
            filledIcons.push({ i, filled: remainingTotal / canonical });
            remainingTotal = 0;
        } else {
            filledIcons.push({ i, filled: 0 });
        }
    }
    const overflow = total > target ? Math.ceil((total - target) / inc) : 0;

    const tapIcon = async (idx) => {
        const f = filledIcons[idx];
        if (f && f.filled >= 1) {
            const last = logs[logs.length - 1];
            setOpenLogId(last?.id || null);
            return;
        }
        setBusy(true);
        try {
            await createQuantityLog(goal.id, iconAmounts[idx]);
            load();
        } finally { setBusy(false); }
    };

    const addCustom = async () => {
        const n = Number(customAmt);
        if (!n || n <= 0) return;
        setBusy(true);
        try {
            await createQuantityLog(goal.id, n);
            setCustomAmt("");
            load();
        } finally { setBusy(false); }
    };

    const openLog = logs.find(l => l.id === openLogId);

    return (
        <div data-testid="quantity-tracker" className="mt-6 p-5 rounded-3xl bg-white border border-ink-10">
            <div className="flex items-baseline justify-between mb-3">
                <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-moss-deep font-semibold">Today</div>
                <button
                    data-testid="toggle-history"
                    onClick={() => setShowHistory(v => !v)}
                    className="font-ui text-[11px] text-ink-40 hover:text-ink"
                >
                    {showHistory ? "Hide previous days" : "Previous days"}
                </button>
            </div>

            <div data-testid="icon-grid" className="flex flex-wrap gap-2 items-center">
                {filledIcons.map(({ i, filled }) => (
                    <button
                        key={i}
                        data-testid={`q-icon-${i}`}
                        data-filled={filled >= 1 ? "yes" : filled > 0 ? "partial" : "no"}
                        onClick={() => tapIcon(i)}
                        disabled={busy}
                        title={`${iconAmounts[i]} ${unit}`}
                        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-ink-10 hover:border-moss/40 transition"
                        style={{
                            background: filled >= 1
                                ? (qc.icon === "water" ? "#9CB0C0" : "#B7CDB1")
                                : filled > 0
                                    ? `linear-gradient(to top, ${qc.icon === "water" ? "#9CB0C0" : "#B7CDB1"} ${filled * 100}%, transparent ${filled * 100}%)`
                                    : "transparent"
                        }}
                    >
                        {qc.icon === "water"
                            ? <Droplet size={14} className={filled >= 1 ? "text-white" : "text-ink-40"} />
                            : <Circle size={12} className={filled >= 1 ? "text-white fill-white" : "text-ink-40"} />}
                    </button>
                ))}
                {Array.from({ length: overflow }, (_, k) => (
                    <span
                        key={`ov-${k}`}
                        data-testid={`q-icon-overflow-${k}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full opacity-70"
                        style={{ background: qc.icon === "water" ? "#9CB0C0" : "#B7CDB1" }}
                        title="Extra"
                    >
                        {qc.icon === "water" ? <Droplet size={14} className="text-white" /> : <Circle size={12} className="text-white fill-white" />}
                    </span>
                ))}
            </div>

            <p data-testid="quantity-summary" className="mt-4 font-ui text-[14px] text-ink">
                <span className="font-medium">{total} {unit} today</span>
                {total < target && <span className="text-ink-40"> · {remaining} to go</span>}
                {total >= target && <span className="text-moss-deep"> · you're there</span>}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                    data-testid="q-custom-input"
                    type="number"
                    min="1"
                    step="1"
                    value={customAmt}
                    onChange={e => setCustomAmt(e.target.value)}
                    placeholder="Amount"
                    className="w-24 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                />
                <button
                    data-testid="q-add-custom"
                    onClick={addCustom}
                    disabled={!customAmt}
                    className={"px-3 py-1.5 rounded-full font-ui text-[12px] " + (customAmt ? "bg-moss text-white hover:bg-moss-deep" : "bg-ink-10 text-ink-40 cursor-not-allowed")}
                >
                    + Custom amount
                </button>
            </div>

            {openLog && (
                <QuantityLogEditor
                    goalId={goal.id}
                    log={openLog}
                    onClose={() => setOpenLogId(null)}
                    onChanged={() => { setOpenLogId(null); load(); }}
                />
            )}

            {logs.length > 0 && (
                <details className="mt-4">
                    <summary className="font-ui text-[11px] text-ink-40 cursor-pointer hover:text-ink">
                        Today's entries · {logs.length}
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {logs.map(l => (
                            <li key={l.id} data-testid={`q-entry-${l.id}`} className="flex items-center gap-2 font-ui text-[12px] text-ink-70">
                                <span>{l.amount} {unit}</span>
                                <span className="text-ink-40">at {fmtHM(l.edited_time || l.logged_at)}</span>
                                <button
                                    onClick={() => setOpenLogId(l.id)}
                                    className="ml-auto text-ink-40 hover:text-ink"
                                    title="Edit"
                                >
                                    <Edit3 size={12} />
                                </button>
                                <button
                                    onClick={async () => { await deleteQuantityLog(goal.id, l.id); load(); }}
                                    className="text-ink-40 hover:text-plum"
                                    title="Delete"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {showHistory && <HistoryList history={history} unit={unit} />}
        </div>
    );
}

function HistoryList({ history, unit }) {
    const [openDate, setOpenDate] = useState(null);
    return (
        <section data-testid="q-history" className="mt-6 pt-4 border-t border-ink-10">
            <h3 className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 font-semibold mb-2">Previous days</h3>
            {history.length === 0 ? (
                <p className="font-ui text-[13px] italic text-ink-40">A record begins here.</p>
            ) : (
                <ul className="space-y-1">
                    {history.map(d => {
                        const open = openDate === d.date;
                        const entries = (d.entries || []).slice().sort((a, b) => {
                            const at = a.edited_time || a.logged_at || "";
                            const bt = b.edited_time || b.logged_at || "";
                            return at.localeCompare(bt);
                        });
                        return (
                            <li key={d.date} data-testid={`q-history-day-${d.date}`}>
                                <button
                                    data-testid={`q-history-toggle-${d.date}`}
                                    aria-expanded={open}
                                    onClick={() => setOpenDate(open ? null : d.date)}
                                    className="w-full flex items-center justify-between gap-3 py-1.5 px-1 rounded-md hover:bg-sage-paper/60 transition font-ui text-[13px] text-ink-70"
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <ChevronRight
                                            size={12}
                                            className={"text-ink-40 transition-transform " + (open ? "rotate-90" : "")}
                                        />
                                        {fmtHistoryDate(d.date)}
                                    </span>
                                    <span className="text-ink">
                                        {Math.round(d.total)} {unit}
                                        <span className="text-ink-40 text-[11px] ml-2">
                                            ({entries.length} {entries.length === 1 ? "entry" : "entries"})
                                        </span>
                                    </span>
                                </button>
                                {open && (
                                    <ul
                                        data-testid={`q-history-entries-${d.date}`}
                                        className="mt-1 mb-2 ml-5 pl-3 border-l border-ink-10 space-y-1"
                                    >
                                        {entries.length === 0 ? (
                                            <li className="font-ui text-[12px] italic text-ink-40">No entries.</li>
                                        ) : entries.map(e => (
                                            <li
                                                key={e.id}
                                                data-testid={`q-history-entry-${e.id}`}
                                                className="font-ui text-[12px] text-ink-70 flex items-baseline gap-2"
                                            >
                                                <span className="text-ink">{e.amount} {unit}</span>
                                                <span className="text-ink-40">at {fmtHM(e.edited_time || e.logged_at)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function QuantityLogEditor({ goalId, log, onClose, onChanged }) {
    const [when, setWhen] = useState(toLocalTimeInput(log.edited_time || log.logged_at));
    const save = async () => {
        const [h, m] = when.split(":").map(Number);
        const iso = new Date();
        iso.setHours(h, m, 0, 0);
        await updateQuantityLog(goalId, log.id, { edited_time: iso.toISOString() });
        onChanged();
    };
    const remove = async () => {
        if (!window.confirm("Delete this log?")) return;
        await deleteQuantityLog(goalId, log.id);
        onChanged();
    };
    return (
        <div data-testid="q-log-editor" className="mt-4 p-3 rounded-2xl bg-sage-paper border border-ink-10">
            <div className="font-ui text-[12px] text-ink-70">
                Logged {log.amount} at
                <input
                    data-testid="q-log-time"
                    type="time"
                    value={when}
                    onChange={e => setWhen(e.target.value)}
                    className="ml-2 px-2 py-1 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                />
            </div>
            <div className="mt-2 flex gap-2 justify-end">
                <button onClick={onClose} className="px-3 py-1 rounded-full font-ui text-[11px] text-ink-70 hover:bg-ink-10">Cancel</button>
                <button onClick={remove} className="px-3 py-1 rounded-full font-ui text-[11px] text-plum hover:bg-plum/10">Delete</button>
                <button data-testid="q-log-save" onClick={save} className="px-3 py-1 rounded-full font-ui text-[11px] bg-moss text-white hover:bg-moss-deep">Save time</button>
            </div>
        </div>
    );
}
